import React, { useState } from 'react';
import { Button, Empty, Spin, Alert } from 'antd';
import { FiMessageSquare, FiRefreshCw } from 'react-icons/fi';
import { TextLog } from '@/types/TCRMLogs';
import { Customer } from '@/types/TCRMLead';
import { TextDetailView } from './TextDetailView';
import { useCRMTextLogs } from 'src/Hooks/SmartCRM/useCRMLogs';

interface TextLogsProps {
	customer: Customer;
	clientID: string;
	isNoviUser?: boolean;
}

export const TextLogs: React.FC<TextLogsProps> = ({ customer, clientID, isNoviUser = false }) => {
	const [selectedText, setSelectedText] = useState<TextLog | null>(null);

	const phoneNumber = customer.number;

	const { textLogs, loading, error, hasMore, fetchTextLogs, loadMore } = useCRMTextLogs(
		clientID,
		phoneNumber,
		isNoviUser,
	);

	if (selectedText) {
		return <TextDetailView text={selectedText} onBack={() => setSelectedText(null)} />;
	}

	if (loading && textLogs.length === 0) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" />
				<span className="ml-2 text-gray-500">Loading text messages...</span>
			</div>
		);
	}

	if (error) {
		return (
			<Alert
				message="Error"
				description={error}
				type="error"
				action={
					<Button size="small" onClick={() => fetchTextLogs(true)}>
						Retry
					</Button>
				}
			/>
		);
	}

	if (textLogs.length === 0) {
		return (
			<div className="text-center py-8">
				<Empty
					description={
						<div>
							<p>No text messages found</p>
							<p className="text-sm text-gray-500 mt-2">Messages to/from: {phoneNumber}</p>
						</div>
					}
				/>
			</div>
		);
	}

	const getDirection = (_text: TextLog) => {
		return 'Outgoing Message';
	};

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<h3 className="text-lg font-medium">Text Messages ({textLogs.length})</h3>
				<Button
					onClick={() => fetchTextLogs(true)}
					loading={loading}
					icon={<FiRefreshCw />}
					type="text"
					size="small"
				>
					Refresh
				</Button>
			</div>

			{textLogs.map((text) => {
				const direction = getDirection(text);

				return (
					<div
						key={text.sid || text.startedTimestamp}
						onClick={() => setSelectedText(text)}
						className="bg-white rounded-lg border p-4 cursor-pointer hover:shadow-md transition-shadow"
					>
						<div className="flex items-center gap-4">
							<div
								className={
									'w-12 h-12 rounded-full flex items-center justify-center text-blue-600 bg-blue-50'
								}
							>
								<FiMessageSquare className="w-6 h-6" />
							</div>
							<div className="flex-1">
								<div className="flex items-center justify-between mb-2">
									<h4 className="font-medium text-gray-900">{direction}</h4>
									<span className="text-sm text-gray-500">
										{new Date(text.startedTimestamp).toLocaleString()}
									</span>
								</div>
								<div className="flex items-center gap-2 mb-2">
									<span className="text-sm text-gray-600">To: {text.to}</span>
									{text.workflow && (
										<span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
											Workflow
										</span>
									)}
								</div>
								<p className="text-sm text-gray-700 line-clamp-3">{text.text}</p>
							</div>
						</div>
					</div>
				);
			})}

			{hasMore && (
				<div className="flex justify-center pt-4">
					<Button onClick={loadMore} loading={loading} type="primary" disabled={!hasMore}>
						{loading ? 'Loading...' : 'Load More'}
					</Button>
				</div>
			)}
		</div>
	);
};
